import * as React from 'react';

import {colors} from '../../colors/colors';
import {Dialog} from '../../dialog/dialog';
import {CancelCircleIcon} from '../../icon/svg/cancel-circle-icon';
import {ClockRegularIcon} from '../../icon/svg/clock-regular-icon';
import {InfoSquareIcon} from '../../icon/svg/info-square-icon';
import {UserOutlineIcon} from '../../icon/svg/user-outline-icon';
import {Stack} from '../../layout/stack/stack';
import {StageIndicator} from '../../stage-indicator/stage-indicator';
import {FormattedTimestamp} from '../../timestamp/formatted-timestamp';
import {Body} from '../../typography/body';

import {BaseRow} from '../base-row/base-row';

import styles from '../automated-email-row/automated-email-row.module.css';

type Props = {
    title: string;
    startTrigger: string | null | undefined;
    nextEditionSendTime: number | null | undefined;
    recipientName: string;
    onSeeDetails: () => void;
    numEditionsSent: number;
    numEditionsTotal: number;
    onCancel?: () => void;
};

export function DripSequenceRowSparse(props: Props) {
    const {onSeeDetails, onCancel} = props;

    const [confirmingCancel, setConfirmingCancel] = React.useState<boolean>(false);
    const cancelButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const toolbarOptions = React.useMemo(() => {
        const options = [
            {
                id: 'details',
                icon: InfoSquareIcon,
                tooltipText: 'Sequence details',
                onClick: onSeeDetails,
            },
        ];

        if (onCancel) {
            options.push({
                id: 'cancel',
                icon: CancelCircleIcon,
                tooltipText: 'Cancel sequence',
                onClick: () => setConfirmingCancel(true),
                // @ts-expect-error
                getRef: (ref) => {
                    cancelButtonRef.current = ref;
                },
            });
        }

        return options;
    }, [onSeeDetails, onCancel]);

    return (
        <>
            <BaseRow
                title={props.title}
                type='drip-sequence'
                onClick={onSeeDetails}
                toolbarProps={{
                    options: toolbarOptions,
                    forceShowToolbar: confirmingCancel,
                }}
            >
                <Stack spacing={4}>
                    <Stack spacing={4} isHorizontal={true} alignItems='center' isFullWidth={true}>
                        {/* @ts-expect-error */}
                        <ClockRegularIcon size={16} color={colors.grey} wrapWithDiv={false} />
                        {props.nextEditionSendTime ? (
                            <Body size='small'>
                                <Stack
                                    spacing={4}
                                    isHorizontal={true}
                                    alignItems='center'
                                    isFullWidth={true}
                                >
                                    Sending next email
                                    <FormattedTimestamp
                                        datetime={props.nextEditionSendTime}
                                        format={FormattedTimestamp.formats.shortMonthDayWithTime}
                                    />
                                </Stack>
                            </Body>
                        ) : (
                            <Body size='small'>{props.startTrigger}</Body>
                        )}
                        <div className={styles['email-progress-container']}>
                            <StageIndicator
                                currentStage={props.numEditionsSent}
                                numStages={props.numEditionsTotal}
                                customColor={colors.reportGreen}
                                height={15}
                            />
                        </div>
                    </Stack>
                    <Stack spacing={4} isHorizontal={true} alignItems='center'>
                        {/* @ts-expect-error */}
                        <UserOutlineIcon size={14} wrapWithDiv={false} color={colors.grey} />
                        <Body size='small'>Sending to {props.recipientName}</Body>
                    </Stack>
                </Stack>
            </BaseRow>
            {confirmingCancel && (
                <Dialog
                    anchor={cancelButtonRef.current}
                    question='Are you sure you want to cancel this drip sequence?'
                    onPrimary={() => {
                        if (onCancel) {
                            onCancel();
                        }
                        setConfirmingCancel(false);
                    }}
                    onSecondary={() => {
                        setConfirmingCancel(false);
                    }}
                    primaryText='Yes, cancel'
                    secondaryText='No, don’t cancel'
                    isWarning={true}
                    customStyles={{width: '230px'}}
                />
            )}
        </>
    );
}
